import { h, Fragment } from "preact";
import {documentToReactComponents, Options} from '@contentful/rich-text-react-renderer';
import { useParams, Link } from "react-router-dom";
import graphql from 'babel-plugin-relay/macro'
import {
    usePreloadedQuery
} from 'react-relay/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListAlt, faLongArrowAltLeft, faLongArrowAltRight } from '@fortawesome/free-solid-svg-icons'
import { QueryRenderer } from 'react-relay';

import { Page } from "../common/Page";
import styled from "styled-components";
import { fontColour, gridSize } from "../style-variables";
import { blogPostsPreloadedQuery, blogpostsQuery } from "../index";
import { ModalDialog } from "../common/ModalDialog";
import { useState} from "preact/hooks";
import { siteQuery, siteQueryResponse } from "../__generated__/siteQuery.graphql";
import { musingsQuery } from "./__generated__/musingsQuery.graphql";
import { GRAPHQL_ENVIRONMENT } from "./fetch-graphql";
import { LoadingIndicator } from "../common/Loading";
import {BLOCKS} from "@contentful/rich-text-types";
import {contentfulRenderOptions, renderNode} from "./contentful-renderers";

const EntryTitle = styled.h1`
    display: flex;
    align-items: center;
    justify-content: space-between; 
    margin: 0;
`;
const EntryMetaData = styled.small`
    font-size: ${gridSize/2}px;
`;

const BlogEntryQuery = graphql`
    query musingsQuery($postId: String!) {
        blog(id:$postId) {
            sys { firstPublishedAt }
            title
            author{ ... on Author { fullName } }
            blogContent { json }
        }
    }
`

const BlogEntry = styled.div`
`
const BlogNavigation = styled.div`
    box-sizing: border-box;
    background: #fff;
    margin: 0 auto;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: ${gridSize * 4}px;
    @media screen and (max-width: 800px) {
       width: 100%;
    }
`;

const BlogNavigationContainer = styled.div`
    background: #fff;
    bottom: 0;
    left: 0;
    position: fixed;
    width: 100%;
`;

const BlogNavigationButton = styled.div`
    box-sizing: border-box;
    flex-basis: 33%;
    font-size: ${gridSize}px;
    opacity: 0.2;
    cursor: pointer;
    transition: opacity 0.5s;
    margin: ${gridSize}px;
    justify-content: center;
    text-align: center;
    display: flex;
    align-items: center;
    &:hover {
        opacity: 1;
    }
`

const BlogNavigationTitle = styled.span`
    font-size: ${gridSize * 0.7};
    overflow: hidden;
    white-space: nowrap;
    margin: 0 ${gridSize}px;
    width: ${gridSize * 5}px;
    text-overflow: ellipsis;
    
      @media screen and (max-width: 800px) {
        display: none;
      }
`

const BlogNavigationLink = styled(Link)`
    justify-content: center;
    text-align: center;
    display: flex;
    text-decoration: none;
    color: ${fontColour};
    align-items: center;
`

const BlogList = styled.ul`
    line-height: 2;
    list-style: none;
    padding: 0;
`

const BlogListItem = styled.li`
    color: ${fontColour};
    opacity: 0.5;
    &:hover {
        opacity: 1;
        transition: opacity 0.5s;
    }
`

const BlogListItemLink = styled(Link)`
    color: ${fontColour};
    text-decoration: none;
`

const PreviousPostButton = ({ postTitle, url }) => <BlogNavigationButton><BlogNavigationLink to={url} title={postTitle}><FontAwesomeIcon icon={faLongArrowAltLeft} /><BlogNavigationTitle>{postTitle}</BlogNavigationTitle></BlogNavigationLink> </BlogNavigationButton>
const BlogListButton = ({ onClick }) => <BlogNavigationButton> <FontAwesomeIcon icon={faListAlt} onClick={onClick}/></BlogNavigationButton>
const NextPostButton = ({ postTitle, url }) => <BlogNavigationButton><BlogNavigationLink title={postTitle} to={url}><BlogNavigationTitle>{postTitle}</BlogNavigationTitle><FontAwesomeIcon icon={faLongArrowAltRight} /> </BlogNavigationLink></BlogNavigationButton>


interface musingsRouteParams {
    postId: string
}

export const Musings = () => {
    const { postId } = useParams<musingsRouteParams>();
    const [isBlogListOpen, setIsBlogListOpen] = useState(false);
    const blogCollectionData: siteQueryResponse = usePreloadedQuery<siteQuery>(blogpostsQuery, blogPostsPreloadedQuery)
    const currentPostIndex = blogCollectionData.blogCollection.items.findIndex((blog) => blog.sys.id === postId);
    const nextPost = blogCollectionData.blogCollection.items[currentPostIndex - 1];
    const previousPost = blogCollectionData.blogCollection.items[currentPostIndex + 1];

    const renderEntry = (entry) => {
        return (
            <BlogEntry>
                <EntryTitle>{entry.title}</EntryTitle>
                <EntryMetaData>
                    Posted on {new Date(entry.sys.firstPublishedAt).toDateString()} by {entry.author.fullName};
                </EntryMetaData>
                <div>
                    {documentToReactComponents(entry.blogContent.json, contentfulRenderOptions)}
                </div>
            </BlogEntry>

        )
    }
    return (
        <Page>
            <QueryRenderer<musingsQuery>
                environment={GRAPHQL_ENVIRONMENT}
                query={BlogEntryQuery}
                variables={{ postId }}
                render={( { error, props}) =>{
                    if(props) {
                        return renderEntry(props.blog)
                    } else {
                        return <LoadingIndicator />
                    }
                }} />
            <ModalDialog
                isOpen={isBlogListOpen}
                onCloseClick={() => setIsBlogListOpen(false)}
                onBlanketClick={() => setIsBlogListOpen(false)}
                header={<h3> Blogposts </h3>}>
                <BlogList>
                    {blogCollectionData.blogCollection.items.map((blogCollectionEntry, index) => {
                        return (
                            <BlogListItem>
                                <BlogListItemLink
                                    to={`/musings/${blogCollectionEntry.sys.id}`}
                                    onClick={() => setIsBlogListOpen(false)}>
                                    {new Date(blogCollectionEntry?.sys?.firstPublishedAt || "").toDateString()} - {blogCollectionEntry.title}
                                </BlogListItemLink>
                            </BlogListItem>
                        )
                    })}
                </BlogList>
            </ModalDialog>
                <BlogNavigation>
                    { previousPost ? <PreviousPostButton url={`/musings/${previousPost.sys.id}`}postTitle={previousPost.title} /> : <BlogNavigationButton />}
                    <BlogListButton onClick={() => setIsBlogListOpen(true)}/>
                    { nextPost ? <NextPostButton url={`/musings/${nextPost.sys.id}`} postTitle={nextPost.title} /> : <BlogNavigationButton /> }
                </BlogNavigation>
        </Page>
    )
};
